import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEstablishments } from "@/context/EstablishmentContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { GoogleBusinessCard } from "@/components/GoogleBusinessCard";

const EMPTY = {
  name: "", sector: "restaurant", has_physical_location: true, former_names_text: "", since_year: "", address: "", city: "", postal_code: "", country: "France",
  website: "", gbp_url: "", instagram: "", facebook: "", tiktok: "", phone: "",
  description: "", booking_url: "", tone: "",
};

export default function EstablishmentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reload, setCurrent } = useEstablishments();
  const [form, setForm] = useState(EMPTY);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(id);

  useEffect(() => {
    api.get("/sectors").then((r) => setSectors(r.data)).catch(() => {});
    if (id) {
      api.get(`/establishments/${id}`).then((r) => {
        const d = r.data; const clean = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => { if (d[k] != null) clean[k] = d[k]; });
        clean.former_names_text = (d.former_names || []).join(", ");
        clean.since_year = d.since_year || "";
        setForm(clean);
      });
    }
  }, [id]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;  // double clic / double soumission : un seul POST
    setLoading(true); setError("");
    const { former_names_text, since_year, ...rest } = form;
    const payload = { ...rest, former_names: (former_names_text || "").split(",").map((x) => x.trim()).filter(Boolean), since_year: since_year ? Number(since_year) : null };
    try {
      if (isEdit) {
        await api.put(`/establishments/${id}`, payload);
        toast.success("Établissement mis à jour");
      } else {
        const { data } = await api.post("/establishments", payload);
        await reload();
        setCurrent(data.id);
        toast.success("Établissement créé — premier audit généré");
      }
      await reload();
      navigate("/app/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const physical = form.has_physical_location !== false;
  const Field = ({ label, k, placeholder, type = "text", required = false }) => (
    <div>
      <Label>{label}{required ? " *" : ""}</Label>
      <Input type={type} value={form[k]} onChange={upd(k)} placeholder={placeholder} required={required} data-testid={`est-${k}-input`} className="mt-1.5" />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-4 text-sm" data-testid="est-form-back">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{isEdit ? "Modifier l'établissement" : "Ajouter un établissement"}</h1>
      <p className="text-slate-500 mt-1">Plus vous renseignez d'informations, plus l'audit est précis.</p>

      <form onSubmit={submit} className="mt-6 bg-white rounded-2xl p-6 border border-slate-200 space-y-5" data-testid="establishment-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Nom de l'établissement *</Label>
            <Input value={form.name} onChange={upd("name")} required data-testid="est-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Secteur d'activité *</Label>
            <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
              <SelectTrigger data-testid="est-sector-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sectors.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
          <Checkbox id="physical" checked={physical} onCheckedChange={(v) => setForm({ ...form, has_physical_location: Boolean(v) })} data-testid="est-physical-checkbox" className="mt-0.5" />
          <div>
            <Label htmlFor="physical" className="cursor-pointer">Établissement physique (accueil de clients sur place)</Label>
            <p className="text-xs text-slate-500 mt-0.5">Adresse, code postal et téléphone deviennent obligatoires : ce sont vos données de référence, comparées à ce que Respondia mesure réellement sur votre site.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {Field({ label: "Adresse", k: "address", required: physical })}
          {Field({ label: "Ville", k: "city", required: true })}
          {Field({ label: "Code postal", k: "postal_code", required: physical })}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {Field({ label: "Pays", k: "country", required: true })}
          {Field({ label: "Téléphone", k: "phone", required: physical })}
          {Field({ label: "Site internet (requis pour l'audit du site)", k: "website", placeholder: "https://" })}
        </div>
        <div className="grid sm:grid-cols-3 gap-4 rounded-xl bg-slate-50 border border-slate-200 p-3">
          <div className="sm:col-span-2">{Field({ label: "Anciennes enseignes / anciens noms (séparés par des virgules)", k: "former_names_text", placeholder: "ex : L'Ancien Bistrot" })}</div>
          {Field({ label: "Année de reprise / ouverture sous ce nom", k: "since_year", type: "number", placeholder: "2023" })}
          <p className="sm:col-span-3 text-xs text-slate-500">Permet à l'IA de ne jamais attribuer à votre enseigne actuelle une récompense ou une information liée à une ancienne enseigne ou à une période antérieure.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {Field({ label: "Fiche Google Business", k: "gbp_url", placeholder: "https://" })}
          {Field({ label: "Lien de réservation", k: "booking_url", placeholder: "https://" })}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {Field({ label: "Instagram", k: "instagram" })}
          {Field({ label: "Facebook", k: "facebook" })}
          {Field({ label: "TikTok", k: "tiktok" })}
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={form.description} onChange={upd("description")} data-testid="est-description-input" className="mt-1.5" rows={3} placeholder="Présentez votre activité en quelques mots" />
        </div>
        {Field({ label: "Ton de communication", k: "tone", placeholder: "ex : chaleureux, professionnel..." })}

        {error && <p className="text-sm text-red-600" data-testid="est-form-error">{error}</p>}
        <div className="flex gap-3">
          <Button type="submit" disabled={loading} aria-busy={loading} data-testid="est-submit-button" className="bg-blue-600 hover:bg-blue-700 rounded-xl">
            {loading ? (isEdit ? "Enregistrement…" : "Création en cours…") : isEdit ? "Enregistrer" : "Créer et auditer"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-xl">Annuler</Button>
        </div>
      </form>
      {isEdit && <div className="mt-6"><GoogleBusinessCard estId={id} onChange={() => {}} /></div>}
    </div>
  );
}
